import { apiClient } from './api'
import { ENDPOINTS } from '@/config/api.config'
import type {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User,
  LoginResult,
  RegisterResult,
  TwoFactorMethod,
  TwoFactorStatus,
  TwoFactorSetupResponse,
  TwoFactorEnableResponse,
} from '@/types'
import { isTwoFactorChallenge, isEmailVerificationPending } from '@/types'

/** Persiste sessão no localStorage (token + user). */
function persistSession(response: AuthResponse) {
  if (response.token) {
    localStorage.setItem('token', response.token)
    localStorage.setItem('user', JSON.stringify(response.user))
  }
}

export const authService = {
  /**
   * Login with email and password.
   * Pode devolver um desafio de 2FA — nesse caso NÃO persiste sessão (o JWT
   * só vem no verifyTwoFactor).
   */
  login: async (credentials: LoginRequest): Promise<LoginResult> => {
    const response = await apiClient.post<LoginResult>(ENDPOINTS.auth.login, credentials)
    if (!isTwoFactorChallenge(response)) {
      persistSession(response)
    }
    return response
  },

  /**
   * Conclui o login informando o código de 2FA (TOTP/OTP/backup).
   */
  verifyTwoFactor: async (challengeId: string, code: string): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>(ENDPOINTS.auth.twoFactor.verify, {
      challengeId,
      code,
    })
    persistSession(response)
    return response
  },

  /** Reenvia o OTP por e-mail de um desafio ativo. */
  resendTwoFactor: async (challengeId: string): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(ENDPOINTS.auth.twoFactor.resend, { challengeId })
  },

  /** Status do 2FA da conta (pro perfil). */
  getTwoFactorStatus: async (): Promise<TwoFactorStatus> => {
    return apiClient.get<TwoFactorStatus>(ENDPOINTS.auth.twoFactor.status)
  },

  /** Inicia o cadastro de um método (OTP por e-mail ou QR do TOTP). */
  setupTwoFactor: async (method: TwoFactorMethod): Promise<TwoFactorSetupResponse> => {
    return apiClient.post<TwoFactorSetupResponse>(ENDPOINTS.auth.twoFactor.setup, { method })
  },

  /** Confirma o código e ativa o 2FA. Devolve backup codes (só TOTP). */
  enableTwoFactor: async (
    method: TwoFactorMethod,
    code: string
  ): Promise<TwoFactorEnableResponse> => {
    return apiClient.post<TwoFactorEnableResponse>(ENDPOINTS.auth.twoFactor.enable, {
      method,
      code,
    })
  },

  /** Desativa o 2FA (exige a senha da conta). */
  disableTwoFactor: async (password: string): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(ENDPOINTS.auth.twoFactor.disable, { password })
  },

  /** Gera novos backup codes (invalida os antigos). */
  regenerateBackupCodes: async (): Promise<{ backupCodes: string[] }> => {
    return apiClient.post<{ backupCodes: string[] }>(ENDPOINTS.auth.twoFactor.regenerateBackupCodes)
  },

  /**
   * Register new user.
   * No cadastro local a conta nasce inativa (aguardando ativação por e-mail) —
   * nesse caso NÃO persiste sessão. Só persiste se vier uma sessão completa.
   */
  register: async (data: RegisterRequest): Promise<RegisterResult> => {
    const response = await apiClient.post<RegisterResult>(ENDPOINTS.auth.register, data)
    if (!isEmailVerificationPending(response)) {
      persistSession(response)
    }
    return response
  },

  /** Ativa a conta a partir do token recebido por e-mail. */
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(ENDPOINTS.auth.emailVerification.verify, { token })
  },

  /** Reenvia o e-mail de ativação. */
  resendVerification: async (email: string): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(ENDPOINTS.auth.emailVerification.resend, { email })
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post(ENDPOINTS.auth.logout)
    } finally {
      // Always clear local storage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    }
  },

  /**
   * Get current user from token
   */
  me: async (): Promise<User> => {
    return apiClient.get<User>(ENDPOINTS.auth.me)
  },

  /**
   * Get current user from localStorage
   */
  getCurrentUser: (): User | null => {
    const userStr = localStorage.getItem('user')
    if (!userStr) return null

    try {
      return JSON.parse(userStr)
    } catch {
      return null
    }
  },

  /**
   * Get current token from localStorage
   */
  getToken: (): string | null => {
    return localStorage.getItem('token')
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('token')
  },

  /**
   * Solicita recuperação de senha
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(ENDPOINTS.auth.forgotPassword, { email })
  },

  /**
   * Valida token de reset de senha
   */
  validateResetToken: async (token: string): Promise<{ valid: boolean; message: string }> => {
    return apiClient.get<{ valid: boolean; message: string }>(
      `${ENDPOINTS.auth.validateResetToken}?token=${token}`
    )
  },

  /**
   * Redefine senha com token
   */
  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(ENDPOINTS.auth.resetPassword, { token, password })
  },

  /**
   * Update user profile
   */
  updateProfile: async (data: {
    name?: string
    email?: string
    currentPassword?: string
    newPassword?: string
  }): Promise<User> => {
    const response = await apiClient.put<User>(ENDPOINTS.users.update, data)

    // Update user in localStorage
    const currentUser = authService.getCurrentUser()
    if (currentUser) {
      const updatedUser = { ...currentUser, ...response }
      localStorage.setItem('user', JSON.stringify(updatedUser))
    }

    return response
  },
}
